import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Dialog, Fab, FormControl, FormHelperText, IconButton, InputLabel, MenuItem, Select, Tooltip, Typography } from '@mui/material';
import { IClient } from "@ontime/types";
import { IAppointment, AppointmentUpdateStatusRequest, IAppointmentUser } from "@ontime/types";
import AppointmentService from '../../services/AppointmentService';
import { useSnackbar } from 'notistack';
import SaveIcon from '@mui/icons-material/Save';
import CheckIcon from '@mui/icons-material/Check';
import AppointmentGuestNameForm from './AppointmentGuestNameForm';
import AppointmentNoteForm from './AppointmentNoteForm';
import AppointmentServiceIdsForm from './AppointmentServiceIdsForm';
import AppointmentDateTimeForm from './AppointmentDateTimeForm';
import ClientList from '../client/ClientList';
import ClearIcon from '@mui/icons-material/Clear';
import PendingIcon from '@mui/icons-material/Pending';
import dayjs from 'dayjs';
import EditIcon from '@mui/icons-material/Edit';
import { AppointmentToCustom, getFullName } from './AppointmentList';
import AppointmentUserList from './AppointmentUserList';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import CustomDialogTitle from '../../customComponents/CustomDialogTitle';
import CustomDialogContent from '../../customComponents/CustomDialogContent';
import { green } from '@mui/material/colors';
import { useTheme, useMediaQuery } from '@mui/material';
import InfoComponent from '../../customComponents/InfoComponent';
import AppointmentPaymentList from './AppointmentPaymentList';
import { useSelector } from 'react-redux';
import { selectSelectedOrganization } from '../../stateManagement/selectedOrganizationSlice';
import { selectSelectedServiceProvider } from '../../stateManagement/selectedServiceProviderSlice';
import { selectOrganizationTasks } from '../../stateManagement/organizationTaskSlice';
import ClientName from '../client/ClientName';
import { AppointmentStatus } from "@ontime/types";

const AppointmentDetailDialog: React.FC<{
    state: { open: boolean; detail?: IAppointment };
    closeAppointmentDialog: (input?: IAppointment) => void;
    AllAppointment: IAppointment[] | [];
    setAllAppointments: (input: IAppointment[]) => void;
}> = ({ state, closeAppointmentDialog, AllAppointment, setAllAppointments }) => {

    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

    const { enqueueSnackbar } = useSnackbar();

    const selectedOrganization = useSelector(selectSelectedOrganization);
    const selectedServiceProvider = useSelector(selectSelectedServiceProvider);


    const [appointment, setAppointment] = useState<IAppointment>({ appointmentId: '', organizationId: selectedOrganization.organizationId, serviceProviderId: selectedServiceProvider.serviceProviderId, status: AppointmentStatus.Pending, guestFirstName: '', version: 0, startDateTime: '', endDateTime: '' });

    const handleClose = () => {
        if (formOpen.open) {
            setFormOpen({ open: false });
        }
        else if (openUser) {
            setOpenUser(false);
        }
        else if (openClient) {
            setOpenClient(false);
        }
        else {
            closeAppointmentDialog();
        }
    }

    const [statusLoading, setStatusLoading] = useState(false);
    const handleUpdateStatus = async (input: AppointmentUpdateStatusRequest) => {

        if ((input.status === AppointmentStatus.Completed || input.status === AppointmentStatus.Confirmed) && !appointment?.endDateTime) {
            enqueueSnackbar('Please set the appointment duration before confirming or completing.', { variant: 'error' });
        }
        else {
            const newStart = dayjs(state.detail?.startDateTime);
            const newEnd = dayjs(state.detail?.endDateTime);

            const isOverlapping = AllAppointment
                .filter((item) => item.status !== AppointmentStatus.Canceled).some(app => {
                    if (state.detail?.appointmentId && app.appointmentId === state.detail?.appointmentId) return false;

                    const existingStart = dayjs(app.startDateTime);
                    const existingEnd = dayjs(app.endDateTime);

                    return newStart.isBefore(existingEnd) && newEnd.isAfter(existingStart);
                });

            if (isOverlapping) {
                enqueueSnackbar('The selected time overlaps with an existing appointment.', { variant: 'error' });
                setLoading(false);
                return;
            }

            setStatusLoading(true);
            try {

                const response = await AppointmentService.UpdateStatus(selectedOrganization.organizationId, selectedServiceProvider.serviceProviderId, input);
                updateExistData({ ...appointment, status: response.status, version: response.version })
            } catch (error) {
                console.error(error, { variant: 'error' });
            } finally {
                setStatusLoading(false);
            }
        }
    };

    const updateExistData = (input: IAppointment) => {
        setAppointment(input);
        setAllAppointments([...AllAppointment.filter((item) => item.appointmentId !== input.appointmentId), AppointmentToCustom(input)]);

        handleDurationAlarm(input);
    };

    const [loading, setLoading] = useState(false);

    const [formOpen, setFormOpen] = useState<{ open: boolean; client?: IClient | null }>({ open: false });

    // const handleSelect = (input: Client) => {
    //     setSelectedClient(input);
    // }

    const organizationTasks = useSelector(selectOrganizationTasks);
    const [durationAlarm, setDurationAlarm] = useState(false);

    const handleDurationAlarm = (input: IAppointment) => {
        const ServicesDuration = (input.serviceIds
            ?.split(',')
            .map((id) => {
                const service = organizationTasks.find((os) => os.organizationTaskId === id);
                return service?.duration ?? 0;
            }) ?? []).reduce((sum, value) => sum + value, 0) ?? 0;

        const appointmentDuration = (dayjs(input.endDateTime ?? input.startDateTime).diff(dayjs(input.startDateTime), 'minute')) ?? 0

        setDurationAlarm(ServicesDuration > appointmentDuration)

    };

    const [clientLoading, setClientLoading] = useState(false);

    const handleSelectClient = async (input?: IClient) => {
        setClientLoading(true);
        setOpenClient(false);

        try {

            var response = await AppointmentService.UpdateClientId(selectedOrganization.organizationId, selectedServiceProvider.serviceProviderId, { appointmentId: appointment.appointmentId, serviceProviderId: selectedServiceProvider.serviceProviderId, clientId: input?.clientId ?? '', version: appointment.version, startDateTime: appointment.startDateTime, endDateTime: appointment.endDateTime });

            if (input) {
                updateExistData({ ...appointment, clientId: input.clientId, version: response.version });
            }
            else {
                updateExistData({ ...appointment, clientId: undefined });
            }
        } catch (error) {
            console.error(error, { variant: 'error' });
        } finally {
            setClientLoading(false);
        }
    };

    const [userLoading, setUserLoading] = useState(false);

    const handleSelectUser = async (input?: IAppointmentUser) => {
        setUserLoading(true);
        setOpenUser(false);

        try {

            const response = await AppointmentService.UpdateUserId(selectedOrganization.organizationId, selectedServiceProvider.serviceProviderId, { appointmentId: appointment.appointmentId, serviceProviderId: selectedServiceProvider.serviceProviderId, userId: input?.appointmentUserId, version: appointment.version, startDateTime: appointment.startDateTime, endDateTime: appointment.endDateTime });

            updateExistData({ ...appointment, userId: input?.appointmentUserId, user: input, version: response.version });
        } catch (error) {
            console.error(error, { variant: 'error' });
        } finally {
            setUserLoading(false);
        }
    };

    const closeAppointmentGuestForm = (input?: IAppointment) => {
        closeAppointmentDialog(input)
    };

    const closeAppointmentByClientIdForm = (input?: IAppointment) => {
        closeAppointmentDialog(input)
    };

    useEffect(() => {
        if (state.open && state.detail) {
            setAppointment(state.detail);
            handleDurationAlarm(state.detail);
        }
        else if (!state.open) {
            setAppointment({ appointmentId: '', organizationId: selectedOrganization.organizationId, serviceProviderId: selectedServiceProvider.serviceProviderId, status: AppointmentStatus.Pending, guestFirstName: '', version: 0, startDateTime: '', endDateTime: '' });
            setOpenClient(false)
            setOpenUser(false);
            setStatusLoading(false);
            setLoading(false);
            setUserLoading(false);
            setClientLoading(false);
        }

    }, [state.open, state.detail]);

    // const [isGuest, setIsGuest] = useState(true);

    const clientIsAddable = (): boolean => {

        if (!appointment.clientId) return true;
        // else if (appointment.userId) return true;
        return false;
    };

    const userIsAddable = (): boolean => {

        if (!appointment.userId) return true;
        return false;
    };

    const [openClient, setOpenClient] = useState(false);
    const [openUser, setOpenUser] = useState(false);

    return (
        <Dialog
            open={state.open}
            onClose={() => closeAppointmentDialog()}
            fullWidth
            fullScreen={fullScreen}
        >
            <CustomDialogTitle handleClose={handleClose} title={`Appointment ${getFullName(appointment)}`} loading={loading || statusLoading || clientLoading || userLoading} />
            {openClient ?
                <ClientList state={openClient ? 'mount' : 'unmount'} loading={loading} setLoading={setLoading} handleSelect={handleSelectClient} userInputFilter={appointment.user} CustomTitle='Select Client' handleClose={handleClose} />
                :
                openUser ?
                    <AppointmentUserList state={'mountByFilter'} loading={loading} setLoading={setLoading} handleSelect={handleSelectUser} handleClose={handleClose} />
                    : null}
            <CustomDialogContent>
                {openClient ?
                    null
                    :
                    openUser ?
                        null
                        :
                        <>
                            <Box display="flex" alignItems="center" gap={2} mt={2}>
                                {clientIsAddable() &&
                                    <InfoComponent infoContent='Convert guest to registered client'>
                                        <Button variant="contained" size='small' onClick={() => setOpenClient(true)} disabled={loading || clientLoading || userLoading || appointment.status === AppointmentStatus.Canceled} ><AssignmentIndIcon /> Add client</Button>
                                    </InfoComponent>
                                }
                                {/* {userIsAddable() &&
                                    <InfoComponent infoContent='Assign a user from your follower list to this appointment'>
                                        <Button variant="contained" size='small' onClick={() => setOpenUser(true)} disabled={loading || clientLoading || userLoading || appointment.status === AppointmentStatus.Canceled} ><AccountCircleIcon /> Add User</Button>
                                    </InfoComponent>
                                } */}

                            </Box>
                            {appointment?.clientId &&
                                <Box display="flex" alignItems="center" gap={0.5} mt={2} height='30px' flexDirection='row' >
                                    <ClientName clientId={appointment?.clientId} />
                                    <InfoComponent infoContent='Remove the assigned client'>
                                        <IconButton disabled={clientLoading || appointment.status === AppointmentStatus.Canceled} size='small' onClick={() => handleSelectClient(undefined)} ><ClearIcon /></IconButton>
                                    </InfoComponent>
                                    <InfoComponent infoContent='Change the assigned client'>
                                        <IconButton disabled={clientLoading || appointment.status === AppointmentStatus.Canceled} size='small' onClick={() => setOpenClient(true)} ><EditIcon /></IconButton>
                                    </InfoComponent>
                                </Box>
                            }

                            {appointment?.userId &&
                                <Box display="flex" alignItems="center" gap={0.5} mt={2} height='30px'>
                                    <Box display='flex' flexDirection='row'>
                                        <AccountCircleIcon />
                                        <Tooltip title={appointment?.user?.phoneNumber ?? ''}>
                                            <Typography variant="body1" gutterBottom>
                                                {appointment?.user?.fullName}
                                            </Typography>
                                        </Tooltip>
                                    </Box>
                                    {appointment?.userId !== appointment?.createdBy &&
                                        <>
                                            <InfoComponent infoContent='Remove the assigned user'>
                                                <IconButton disabled={userLoading || appointment.status === AppointmentStatus.Canceled} size='small' onClick={() => { handleSelectUser(undefined) }} ><ClearIcon /></IconButton>
                                            </InfoComponent>
                                            <InfoComponent infoContent='Change the assigned user'>
                                                <IconButton disabled={userLoading || appointment.status === AppointmentStatus.Canceled} size='small' onClick={() => setOpenUser(true)} ><EditIcon /></IconButton>
                                            </InfoComponent>
                                        </>
                                    }
                                </Box>
                            }
                            {(userIsAddable() && !appointment.clientId && !clientLoading && !userLoading) &&
                                <AppointmentGuestNameForm
                                    appointment={appointment}
                                    closeForm={closeAppointmentGuestForm}
                                    updateExistData={updateExistData}
                                    disabled={appointment.status === AppointmentStatus.Canceled}
                                />
                            }
                            <AppointmentDateTimeForm
                                appointment={appointment}
                                closeForm={closeAppointmentGuestForm}
                                updateExistData={updateExistData}
                                AllAppointment={AllAppointment}
                                disabled={appointment.status === AppointmentStatus.Canceled}
                            />
                            <AppointmentNoteForm
                                appointment={appointment}
                                closeForm={closeAppointmentGuestForm}
                                updateExistData={updateExistData}
                                disabled={appointment.status === AppointmentStatus.Canceled}
                            />
                            <AppointmentServiceIdsForm
                                appointment={appointment}
                                closeForm={closeAppointmentGuestForm}
                                updateExistData={updateExistData}
                                disabled={appointment.status === AppointmentStatus.Canceled}
                            />
                            <Box display="flex" alignItems="center" gap={2} mt={2}>
                                <FormControl fullWidth >
                                    <InputLabel>Status</InputLabel>
                                    <Select
                                        label="Status"
                                        value={appointment.status}
                                        onChange={(e) =>
                                            handleUpdateStatus({
                                                appointmentId: appointment?.appointmentId ?? '',
                                                serviceProviderId: selectedServiceProvider.serviceProviderId,
                                                status: Number(e.target.value) as AppointmentStatus,
                                                version: appointment.version,
                                                startDateTime: appointment.startDateTime,
                                                endDateTime: appointment.endDateTime,
                                            })
                                        }
                                        disabled={statusLoading || appointment.status === AppointmentStatus.Canceled}
                                    >
                                        <MenuItem value={AppointmentStatus.Pending}>Pending</MenuItem>
                                        <MenuItem value={AppointmentStatus.Confirmed}>Confirmed</MenuItem>
                                        <MenuItem value={AppointmentStatus.Canceled}>Canceled</MenuItem>
                                        <MenuItem value={AppointmentStatus.Completed}>Completed</MenuItem>
                                    </Select>
                                </FormControl>
                                <Box sx={{ position: 'relative' }}>
                                    <Fab disabled={true} size='small' aria-label="save" color="primary" type="submit">
                                        {!statusLoading ? <CheckIcon /> : <SaveIcon />}
                                    </Fab>
                                    {statusLoading && (
                                        <CircularProgress
                                            size={48}
                                            sx={{
                                                color: green[500],
                                                position: 'absolute',
                                                top: -4,
                                                left: -4,
                                                zIndex: 1,
                                            }}
                                        />
                                    )}
                                </Box>
                            </Box>
                            <Box display="flex" alignItems="center" gap={2} mt={2}>
                                {appointment.status === AppointmentStatus.Canceled &&
                                    <Button variant="contained" size='small' onClick={() => handleUpdateStatus({ appointmentId: appointment?.appointmentId ?? '', serviceProviderId: selectedServiceProvider.serviceProviderId, status: AppointmentStatus.Pending, version: appointment.version, startDateTime: appointment.startDateTime, endDateTime: appointment.endDateTime })} disabled={statusLoading} ><PendingIcon />Uncanceled</Button>}
                            </Box>
                            {durationAlarm && <FormHelperText error sx={{ ml: 2 }}>Base selected services you need more duration time.</FormHelperText>}
                        </>
                }

            </CustomDialogContent>
            <AppointmentPaymentList
                appointmentId={state.detail?.appointmentId ?? ''}
                clientId={state.detail?.clientId ?? ''}
                organizationId={state.detail?.organizationId ?? ''}
                disabled={appointment.status === AppointmentStatus.Canceled}
                dontShow={!openClient && !openUser}
                appointmentAmount={appointment.amount}
            />
        </Dialog>
    );
};

export default AppointmentDetailDialog;